import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import {
	callNativeWeb,
	clickSecurityKeypadEventWeb,
	footerShowEventWeb,
	hideProgressWeb,
	showProgressWeb
} from 'common/appInterface';
import CardApplyStopModal from 'components/CardApplyStopModal';
import Modal from 'components/Modal';
import { getVersionUrl } from 'common/cacheOverwrite';

const CustomerInfoPage: React.FC = () => {
	/** Location State */
	const location = useLocation() as Location<LocationState>;
	/** Navigate */
	const navigate = useNavigate();
	/** 다국어처리 */
	const {t, i18n} = useTranslation();
	
	const screenId = "APPL10";
	const screenName = t("APPL10.title02", "Set Card Password");
	
	const [cardApply, setCardApply] = useState<Partial<CardApplyStep5>>({
		...location.state.apply,
		userEngName: location.state.apply.userEngName?.replace(/[^A-Za-z\s]/ig, '').toUpperCase()
	});
	const [passwordInput, setPasswordInput] = useState("");
	const [passwordConfirm, setPasswordConfirm] = useState("");
	const [passwordCheckCode, setPasswordCheckCode] = useState("");
	const [isClick, setClick] = useState(false);
	
	const passwordRef = useRef<HTMLParagraphElement>(null);
	const passwordConfirmRef = useRef<HTMLParagraphElement>(null);
	
	const [isStop, setStop] = useState(false);
	const closeStop = useCallback(() => setStop(false), []);

	/** 오류 안내 웹 팝업 (시스템 팝업 callAlertWeb 대신) */
	const [isAlert, setAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	const openAlert = useCallback((message: string) => {
		setAlertMessage(message);
		setAlert(true);
	}, []);

	const closeAlert = useCallback(() => setAlert(false), []);
	
	/** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
	const callbackBackkeyEvent = useCallback(() => {
		setStop(true);
	}, []);
	
	/** 보안키패드 입력요청 (clickSecurityKeypadEvent_Web (WEB > APP)) */
	const clickSecurityKeypadEvent = useCallback((type: string, title: string) => {
		clickSecurityKeypadEventWeb(screenId, screenName, type, "4", title);

		setTimeout(() => {
			hideProgressWeb();
		}, 1500);
	}, [screenName]);
	
	/** 보안키패드 입력 (typingSecurityKeypad_App (APP > WEB)) */
	const callbackTypingSecurityKeypadApp = useCallback((event) => {
		if (event.detail.keypadCode === "2") {
			setPasswordInput(Array<string>(Number.parseInt(event.detail.length)).fill("*").join(""));
		} else if (event.detail.keypadCode === "5") {
			setPasswordConfirm(Array<string>(Number.parseInt(event.detail.length)).fill("*").join(""));
		}
	}, []);
	
	/** 보안키패드 입력완료 처리 (completedPincode_App (APP > WEB)) */
	const callbackCompletedSecurityKeypadApp = useCallback((event) => {
		// 하단바 숨기기
		footerShowEventWeb("N");
		
		let length = event.detail.length === "0" ? 4 : Number.parseInt(event.detail.length);
		
		if (event.detail.keypadCode === "2") {
			setPasswordInput(Array<string>(length).fill("*").join(""));
			passwordRef.current?.classList.remove("error");
			setCardApply({
				...cardApply,
				passwordKeypadPid: event.detail.keypadPid,
				passwordEnteredValue: event.detail.enteredValue
			})
			
			client.post("/api/app/manage/password/check", {
				...location.state.query,
				passwordKeypadPid: event.detail.keypadPid,
				passwordEnteredValue: event.detail.enteredValue
			}).then((response) => {
					if (response.data.code === "00" || response.data.code === "0000") {
						setPasswordCheckCode(response.data.response.checkResult);
					}
				}
			)
		} else if (event.detail.keypadCode === "5") {
			setPasswordConfirm(Array<string>(length).fill("*").join(""));
			passwordConfirmRef.current?.classList.remove("error");
			setCardApply({
				...cardApply,
				confirmKeypadPid: event.detail.keypadPid,
				confirmEnteredValue: event.detail.enteredValue
			})
		}
	}, [location.state.query, cardApply]);

	/** Next 처리 */
	const clickNext = useCallback(() => {
		if (isClick) {
			console.log("Already Clicked...");
			return;
		}
		
		setClick(true);
		showProgressWeb();
		
		passwordRef.current?.classList.remove("error");
		passwordConfirmRef.current?.classList.remove("error");
		
		// 패스워드 검증
		client.patch("/api/app/apply/password/verify", {
			...location.state.query,
			receiveSeq: cardApply.receiveSeq,
			receiveDate: cardApply.receiveDate,
			passwordKeypadPid: cardApply.passwordKeypadPid,
			passwordEnteredValue: cardApply.passwordEnteredValue,
			confirmKeypadPid: cardApply.confirmKeypadPid,
			confirmEnteredValue: cardApply.confirmEnteredValue
		}).then((response) => {
			console.log(response);
			if (response.data.code === "00") {
				hideProgressWeb();
				setClick(false);

				// 추천 직원(초대코드) 입력으로 이동 — 신청내역(06) 등록은 다음 화면에서 처리
				navigate("/apply/step5/proposer", {
					state: {
						...location.state,
						apply: cardApply
					}
				});
			} else {
				hideProgressWeb();
				setClick(false);
				
				if (response.data.code === "91") {
					passwordRef.current?.classList.add("error");
				} else if (response.data.code === "92") {
					passwordConfirmRef.current?.classList.add("error");
				} else {
					openAlert(response.data.message);
				}
			}
		}).catch((e: AxiosError) => {
			hideProgressWeb();
			setClick(false);
			openAlert(e.message);
		});
	}, [isClick, location.state, cardApply, navigate, openAlert]);
	
	// TODO TEST입력
	const testCall = (type: string) => {
		let keypadPid = "TEST_PID";
		let enteredValue = "TEST_ENTER_VALUE";
		
		if (type === "2") {
			keypadPid = "C1DD17010D5940E587861BDDDDAF544A";
			enteredValue = "c4ae4a276119237d1e0bf4793c7c473cbb20b93bae9b65ebd58f191c5f33e6ecd7cb716efee09270cf61fc08b594bc15a9d9766a4edff52e62f39335e197954e63e16656246a7b5de30457093d885277e601d799";
		} else if (type === "5") {
			keypadPid = "3EDCA9F3EE1A404C8AB6DE78E984AA81";
			enteredValue = "5e59b043e355e71bb7d8a46a26a39dc8ae755d7c017e59276744aa8b09fe76d23b8153f6d004e234a2010d1c0596434ed4755e972727ae34cfc16b07382577c23012de7f9d5a1d7b6d0fe4433b05199832a969e4";
		}
		
		let event = new CustomEvent("completKeypad", {
			detail: {
				keypadPid: keypadPid,
				keypadCode: type,
				enteredValue: enteredValue,
				length: "4"
			}
		});
		document.dispatchEvent(event);
	}
	
	// 최초실행
	useEffect(() => {
		// 언어설정
		i18n.changeLanguage(location.state.query.languageCode);
		// callNative_Web 처리 (WEB > APP)
		callNativeWeb(screenId, screenName);
		// 하단바 숨기기
		footerShowEventWeb("N");
		
		console.log("QUERY STATE");
		console.log(location.state);
		console.log(cardApply);

		// 최초한번실행 useEffect() Warning 제거
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	// 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("backpress", callbackBackkeyEvent);
		return () => document.removeEventListener("backpress", callbackBackkeyEvent);
	}, [callbackBackkeyEvent]);
	
	// 보안키패드 입력 이벤트 리스너 (typingSecurityKeypad_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("typingKeypad", callbackTypingSecurityKeypadApp);
		return () => document.removeEventListener("typingKeypad", callbackTypingSecurityKeypadApp);
	}, [callbackTypingSecurityKeypadApp]);
	
	// 보안키패드 결과전달 이벤트 리스너 (completedSecurityKeypad_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("completKeypad", callbackCompletedSecurityKeypadApp);
		return () => document.removeEventListener("completKeypad", callbackCompletedSecurityKeypadApp);
	}, [callbackCompletedSecurityKeypadApp]);

	return (
		<section className="pdb70">
			<div id="wrap">
				<div id="container" className="pcHidden">
					<section className="contents cardPassword">
						<h2 className="heading">{t('APPL10.explan08', 'Set your card password')}</h2>
						<div className="form">
							<div className="group">
								{/* Card Password */}
								<div className="inputField">
									<p className="label">{t('APPL10.header04', 'Card Password')}</p>
									<div className="inputBox" onClick={() => clickSecurityKeypadEvent('2', t('APPL10.header04', 'Card Password'))}>
										<input type="password" value={passwordInput} maxLength={4} readOnly placeholder={t('APPL10.placeholder01', 'Enter 4 digits')} />
									</div>
								</div>
								{/* 비밀번호 규칙 안내 (서버 검사 결과에 따라 아이콘 변경) */}
								<ul className="helper">
									<li className={passwordCheckCode === '' ? '' : (passwordCheckCode === '10' || passwordCheckCode === '11' ? 'fail' : 'pass')}>
										{
											(passwordCheckCode !== '') &&
												<img alt="" src={getVersionUrl(passwordCheckCode === '10' || passwordCheckCode === '11' ? '/assets/images/ico/ic_ch_disabled_16.png' : '/assets/images/ico/ic_ch_enabled_16.png')} />
										}
										{
											(passwordCheckCode === '') &&
												<img alt="" src={getVersionUrl("/assets/images/ico/ic_ch_16.png")} />
										}
										<p>{t('APPL10.explan05.01', 'Do not contain consecutive or identical numbers(1111, 1234)')}</p>
									</li>
									<li className={passwordCheckCode === '' ? '' : (passwordCheckCode === '01' || passwordCheckCode === '11' ? 'fail' : 'pass')}>
										{
											(passwordCheckCode !== '') &&
												<img alt="" src={getVersionUrl(passwordCheckCode === '01' || passwordCheckCode === '11' ? '/assets/images/ico/ic_ch_disabled_16.png' : '/assets/images/ico/ic_ch_enabled_16.png')} />
										}
										{
											(passwordCheckCode === '' || passwordCheckCode === '91') &&
												<img alt="" src={getVersionUrl("/assets/images/ico/ic_ch_16.png")} />
										}
										<p>{t('APPL10.explan05.02', 'Do not contain ID Number, Date of Birth, Phone Number, Card Number')}</p>
									</li>
								</ul>
							</div>
							{/* Re-enter Card Password */}
							<div className="inputField">
								<p className="label">{t("APPL10.header05", 'Re-enter Card Password')}</p>
								<div className="inputBox" onClick={() => clickSecurityKeypadEvent("5", t("APPL10.header05", 'Re-enter Card Password'))}>
									<input type="password" value={passwordConfirm} maxLength={4} readOnly placeholder={t('APPL10.placeholder02', 'Re-enter 4 digits')} />
								</div>
							</div>
						</div>
						{
							process.env.REACT_APP_PROFILE !== "prod" &&
								<>
										<button className="btnPrimaryTy01" onClick={() => testCall("2")}>
												<span>카드비밀번호 입력</span>
										</button>
										<button className="btnPrimaryTy01" onClick={() => testCall("5")}>
												<span>카드비밀번호 확인 입력</span>
										</button>
								</>
						}
					</section>
					<div className="btnArea">
						<div className="btnWrapTy01">
							<button type="button" className="btnPrimaryTy01"
											disabled={stringUtils.checkValueNull(cardApply.userEngName) || stringUtils.checkValueNull(cardApply.passwordEnteredValue) || stringUtils.checkValueNull(cardApply.confirmEnteredValue) || passwordCheckCode !== '00'}
											onClick={clickNext}>
								<span>{t("COMMON.button.next")}</span>
							</button>
						</div>
					</div>
				</div>
				{/* 신청 중단 팝업 */}
				<CardApplyStopModal isStop={isStop} closeStop={closeStop}/>
				{/* 비밀번호 검증 오류 팝업 (디자인 변경 요청) 2026.10.16 */}
				<Modal isOpen={isAlert} onRequestClose={closeAlert} contentLabel="modal" className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}>
					<p className="message">{alertMessage}</p>
					<div className="btnWrapTy02">
						<button type="button" className="btnPrimaryTy01" onClick={closeAlert}><span>{t("COMMON.button.ok")}</span></button>
					</div>
				</Modal>
			</div>
		</section>
	);
};

export default CustomerInfoPage;